// "View LoanTape" - ported from reporting-hub/frontend/src/components/
// preflight/loantape-preview/{LoanTapeViewerDialog,LoanTapePreviewTable,
// useLoanTapeViewerState,LoanCashflowsPanel}.tsx, folded into this one file
// per the migration brief (only ColumnFilterPopover stays a separate file,
// matching the original's own split). The one place the LoanTape can be
// viewed from - opened either via a persistent "View LoanTape" button or via
// a "jump to row" arrow next to a failed check's affected loan ID.
//
// ============================================================================
// MOUNTING CONTRACT - read this before wiring this component in elsewhere
// ============================================================================
// Props: { reportingId: string, open: boolean, onOpenChange: (open: boolean)
// => void, onRegisterJump: (jump: (identifier: string) => void) => void,
// snapshotVersion?: any (e.g. status.validation.validated_at - a change
// refetches the snapshot on next open) }
//
// Data: the whole LoanTape snapshot the Validate job wrote (GET
// .../loantape-snapshot -> presigned URL -> gzip JSON) is loaded once when
// the dialog first opens and cached for the dialog's lifetime; search,
// column filters, filter options, "Load more" and jump-to-row all run
// client-side via loantapeFilters.js (ports of the old server endpoints).
//
// This component must be MOUNTED UNCONDITIONALLY by its host (regardless of
// `open`) - not `{open && <LoanTapeViewerDialog .../>}`. Reason: the host
// calls `onRegisterJump` once (via a useEffect) to receive this dialog's
// internal `jumpToRow(identifier)` function, stashes it (e.g. in a ref), and
// later - e.g. from a failed check's "Affected rows" table - calls that
// function AND sets `open=true` together to jump straight to a specific loan
// with its row highlighted/scrolled into view. If the host only mounts this
// component while `open` is already true, `onRegisterJump` never fires in
// time and "jump to row" has nothing to call. The dialog's own search request
// is still gated on `open` internally (skipped while closed), so mounting it
// unconditionally does not cost an extra fetch. A jump requested while the
// snapshot is still loading is applied as soon as it arrives.
//
// Example host wiring:
//   const jumpRef = useRef(() => {});
//   const [loanTapeOpen, setLoanTapeOpen] = useState(false);
//   ...
//   <LoanTapeViewerDialog
//     reportingId={reportingId}
//     open={loanTapeOpen}
//     onOpenChange={setLoanTapeOpen}
//     onRegisterJump={(jump) => { jumpRef.current = jump; }}
//   />
//   // elsewhere, e.g. an "Affected rows" table's per-row jump arrow:
//   <Button icon="pi pi-arrow-right" onClick={() => { jumpRef.current(investmentKey); setLoanTapeOpen(true); }} />
// ============================================================================

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Chip } from 'primereact/chip';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Skeleton } from 'primereact/skeleton';
import { Toast } from 'primereact/toast';
import axiosWithAuth from '../../../helpers/AxiosHelper';
import ColumnFilterPopover from './ColumnFilterPopover';
import { findRow, searchRows } from './loantapeFilters';
import { readErrorDetail } from '../jobs';
import { formatCurrency } from '../calculation/calcFormat';
import errorMessage from '../../../helpers/errorMessage';

const IDENTIFIER_COLUMN = 'investment_key';
// A 200-row page with ~47 columns is ~9,400 custom-rendered cells plus one
// stateful filter-popover per column header, all non-virtualized - enough
// to make the whole page feel sluggish (not just this dialog) while it
// renders. 50 keeps the same search/filter/"Load more" UX with a much
// cheaper initial render; PrimeReact virtualScroller would help more but
// has real compatibility caveats with row expansion (used here for the
// per-loan CashFlows panel), so isn't worth the risk without being able to
// visually verify it.
const PAGE_SIZE = 50;
const LOAD_MORE_INCREMENT = 50;
const SEARCH_DEBOUNCE_MS = 300;
const ALL_COLUMNS_VALUE = '__all__';

function formatPlain(value) {
  if (value === null || value === undefined) return '–';
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  return String(value);
}

function formatCellValue(value) {
  return typeof value === 'number' ? formatCurrency(value) : formatPlain(value);
}

function cellMatchesQuery(value, query) {
  if (!query) return false;
  return String(value ?? '').toLowerCase().includes(query.toLowerCase());
}

// Splits `display` into plain-text/matched segments around the first
// case-insensitive occurrence of `query`, tolerating thousands-separator
// commas inserted by formatCurrency (so query "1600" still matches inside
// "1,600.50"). Returns null if no literal match exists in `display` at all.
function highlightMatch(display, query) {
  const stripped = display.replace(/,/g, '');
  const strippedIdx = stripped.toLowerCase().indexOf(query.toLowerCase());
  if (strippedIdx === -1) return null;
  let displayIdx = 0;
  let strippedSeen = 0;
  while (strippedSeen < strippedIdx) {
    if (display[displayIdx] !== ',') strippedSeen++;
    displayIdx++;
  }
  let endDisplayIdx = displayIdx;
  let matchedChars = 0;
  while (matchedChars < query.length && endDisplayIdx < display.length) {
    if (display[endDisplayIdx] !== ',') matchedChars++;
    endDisplayIdx++;
  }
  const before = display.slice(0, displayIdx);
  const match = display.slice(displayIdx, endDisplayIdx);
  const after = display.slice(endDisplayIdx);
  return (
    <>
      {before}
      <mark style={{ borderRadius: 2, padding: '0 2px' }}>{match}</mark>
      {after}
    </>
  );
}

function summarizeFilter(filter) {
  if (filter.kind === 'values') {
    const count = (filter.values || []).length;
    return `${count} value${count === 1 ? '' : 's'} selected`;
  }
  const min = filter.min ?? '…';
  const max = filter.max ?? '…';
  return `${min} – ${max}`;
}

// Expanded inline under a LoanTape row when its expander is toggled - every
// CashFlows row for that one loan (matched by investment_key).
function LoanCashflowsPanel({ reportingId, investmentKey }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!reportingId || !investmentKey) return;
    let cancelled = false;
    setLoading(true);
    axiosWithAuth
      .get(`/compartment-report/reportings/${reportingId}/cashflows-for-loan`, { params: { investment_key: investmentKey } })
      .then((res) => {
        if (!cancelled) {
          setData(res.data);
          setError(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('[LoanCashflowsPanel] fetch error:', errorMessage(err));
          setError(true);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reportingId, investmentKey]);

  // Matches the outer LoanTape table's look (size="small", showGridlines)
  // instead of ad hoc smaller font/padding - same look, just with a light
  // brand tint (design tokens, so it also works in dark mode) on the
  // wrapping panel as the one deliberate difference, so it still reads as
  // "this is the nested CashFlows panel for one loan" rather than another
  // LoanTape row.
  const thStyle = {
    position: 'sticky',
    top: 0,
    whiteSpace: 'nowrap',
    padding: '0.5rem 0.5rem',
    textAlign: 'left',
    fontSize: '12px',
    fontWeight: 600,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    color: 'var(--app-text-muted)',
    background: 'var(--app-surface-2)',
    border: '1px solid var(--app-border)',
  };
  const tdStyle = (value) => ({
    whiteSpace: 'nowrap',
    padding: '0.5rem 0.5rem',
    border: '1px solid var(--app-border)',
    background: 'var(--app-surface)',
    fontVariantNumeric: 'tabular-nums',
    textAlign: typeof value === 'number' ? 'right' : 'left',
  });

  return (
    <div className="p-2 border-round" style={{ backgroundColor: 'var(--app-primary-softer)' }}>
      {loading && <Skeleton height="5rem" />}
      {error && <div className="ex-inline-error">Failed to load cashflows for this loan.</div>}
      {data && data.rows.length === 0 && <p className="text-color-secondary m-0">No cashflows found for this loan.</p>}
      {data && data.rows.length > 0 && (
        <div style={{ maxHeight: '16rem', overflow: 'auto', width: 'fit-content', maxWidth: '100%' }}>
          {/* Deliberately NOT className="p-datatable-table" - that name
              collides with PrimeReact's own theme CSS for the outer table
              (which assumes Column-managed per-column widths), and applying
              it to this plain hand-built table with no such widths caused
              every header cell to be squeezed into an equal auto-share of
              the table width, overlapping the whitespace-nowrap content
              badly. `tableLayout: 'auto'` is set explicitly (belt-and-
              suspenders) so column widths are driven purely by this table's
              own content, independent of any global class. */}
          <table style={{ borderCollapse: 'collapse', tableLayout: 'auto' }}>
            <thead>
              <tr>
                {data.columns.map((col) => (
                  <th key={col} style={thStyle}>
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.rows.map((row, rowIdx) => (
                <tr key={rowIdx}>
                  {row.map((value, colIdx) => (
                    <td key={colIdx} style={tdStyle(value)}>
                      {formatCellValue(value)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const LoanTapeViewerDialog = ({ reportingId, open, onOpenChange, onRegisterJump, snapshotVersion = null }) => {
  const [query, setQueryRaw] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [column, setColumnRaw] = useState(null);
  const [activeFilters, setActiveFiltersRaw] = useState([]);
  const [limit, setLimit] = useState(PAGE_SIZE);
  const [injectedRows, setInjectedRows] = useState([]);
  const [highlightedKey, setHighlightedKey] = useState(null);
  const [expandedKey, setExpandedKey] = useState(null);

  const [snapshot, setSnapshot] = useState(null);
  const [snapshotLoading, setSnapshotLoading] = useState(false);
  const [snapshotError, setSnapshotError] = useState(null);
  const [pendingJump, setPendingJump] = useState(null);
  const loadedKeyRef = useRef(null);

  const toast = useRef(null);
  const rowRefs = useRef(new Map());

  // Debounce the search query 300ms after typing stops.
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedQuery(query), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [query]);

  // Load the snapshot once, the first time the dialog opens for this
  // reporting (and again only if reportingId/snapshotVersion changes).
  // Gated on `open` - this dialog is mounted unconditionally by its host so
  // "jump to row" keeps working while closed (see the mounting contract
  // above).
  const snapshotKey = `${reportingId}|${snapshotVersion ?? ''}`;
  useEffect(() => {
    if (!open || !reportingId || loadedKeyRef.current === snapshotKey) return;
    let cancelled = false;
    loadedKeyRef.current = snapshotKey;
    setSnapshot(null);
    setSnapshotError(null);
    setSnapshotLoading(true);
    axiosWithAuth
      .get(`/compartment-report/reportings/${reportingId}/loantape-snapshot`)
      .then((res) => fetch(res.data.url))
      .then((res) => {
        if (!res.ok) throw new Error(`LoanTape snapshot download failed (HTTP ${res.status})`);
        return res.json();
      })
      .then((data) => {
        if (!cancelled) setSnapshot(data);
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('[LoanTapeViewerDialog] snapshot error:', errorMessage(err));
          loadedKeyRef.current = null; // allow a retry on next open
          setSnapshotError(readErrorDetail(err, 'Failed to load the LoanTape.'));
        }
      })
      .finally(() => {
        if (!cancelled) setSnapshotLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, reportingId, snapshotKey]);

  // Same shape the old GET .../loantape-search returned. searchRows treats
  // the query as a regex (pandas str.contains did) and throws on an invalid
  // one.
  const { searchData, searchError } = useMemo(() => {
    if (!snapshot) return { searchData: null, searchError: null };
    try {
      return {
        searchData: searchRows(snapshot, { query: debouncedQuery, column, limit, filters: activeFilters }),
        searchError: null,
      };
    } catch (err) {
      return { searchData: null, searchError: err instanceof SyntaxError ? 'Invalid search pattern' : 'Search failed.' };
    }
  }, [snapshot, debouncedQuery, column, limit, activeFilters]);
  // An invalid pattern keeps the last good results on screen (like the old
  // failed request did) with the error shown above them.
  const lastGoodSearchRef = useRef(null);
  if (searchData) lastGoodSearchRef.current = searchData;
  if (!snapshot) lastGoodSearchRef.current = null;
  const data = searchData || (searchError ? lastGoodSearchRef.current : null);

  const columns = useMemo(() => (data && data.columns) || [], [data]);
  const idColumnIndex = columns.indexOf(IDENTIFIER_COLUMN);
  const hasIdentifier = idColumnIndex >= 0;
  const addedSet = useMemo(() => new Set((data && data.added_columns) || []), [data]);

  const rows = useMemo(() => [...injectedRows, ...((data && data.rows) || [])], [injectedRows, data]);
  const injectedKeys = useMemo(
    () => new Set(idColumnIndex >= 0 ? injectedRows.map((r) => String(r[idColumnIndex])) : []),
    [injectedRows, idColumnIndex]
  );

  const tableRows = useMemo(
    () =>
      rows.map((row, rowIdx) => {
        const key = idColumnIndex >= 0 ? String(row[idColumnIndex]) : `idx-${rowIdx}`;
        const obj = { _key: key, _injected: injectedKeys.has(key), _highlighted: highlightedKey === key };
        columns.forEach((c, ci) => {
          obj[c] = row[ci] ?? null;
        });
        return obj;
      }),
    [rows, columns, idColumnIndex, injectedKeys, highlightedKey]
  );

  // useCallback with stable (setState) deps only, so the identity stays
  // fixed across renders - `setActiveFilter` is passed as `onApply` to every
  // column's ColumnFilterPopover (React.memo'd), and an unstable reference
  // here would make that memo a no-op on every keystroke in the search box.
  const resetPaging = useCallback(() => {
    setHighlightedKey(null);
    setInjectedRows([]);
    setLimit(PAGE_SIZE);
  }, []);

  const setQuery = (next) => {
    setQueryRaw(next);
    resetPaging();
  };
  const setColumn = (next) => {
    setColumnRaw(next);
    resetPaging();
  };
  const setActiveFilter = useCallback(
    (filter, forColumn) => {
      setActiveFiltersRaw((prev) => {
        const withoutColumn = prev.filter((f) => f.column !== forColumn);
        const next = filter ? [...withoutColumn, filter] : withoutColumn;
        return next.sort((a, b) => a.column.localeCompare(b.column));
      });
      resetPaging();
    },
    [resetPaging]
  );
  const clearAllFilters = () => {
    setActiveFiltersRaw([]);
    resetPaging();
  };
  const loadMore = () => setLimit((prev) => prev + LOAD_MORE_INCREMENT);

  const scrollToKey = (key) => {
    requestAnimationFrame(() => {
      const el = rowRefs.current.get(key);
      el?.closest('tr')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  };

  // Jumps to a specific loan from a failed check's "Affected rows" table -
  // clears any active search first so the row's normal context is visible,
  // then injects the row (looked up in the snapshot) at the top. May be
  // called while the dialog is still closed / the snapshot still loading -
  // the effect below applies it once the snapshot is there.
  const jumpToRow = (identifier) => {
    if (!reportingId) return;
    setQueryRaw('');
    setDebouncedQuery('');
    setColumnRaw(null);
    setActiveFiltersRaw([]);
    setLimit(PAGE_SIZE);
    setPendingJump({ identifier: String(identifier) });
  };

  useEffect(() => {
    if (!pendingJump) return;
    if (snapshotError) {
      setPendingJump(null);
      return;
    }
    if (!snapshot) return;
    const { identifier } = pendingJump;
    setPendingJump(null);
    const row = findRow(snapshot, identifier);
    if (!row) {
      toast.current?.show({ severity: 'warn', summary: `Loan ${identifier} not found in the LoanTape`, life: 5000 });
      return;
    }
    const lookupIdIdx = snapshot.columns.indexOf(IDENTIFIER_COLUMN);
    setInjectedRows((prev) => [row, ...prev.filter((r) => lookupIdIdx < 0 || String(r[lookupIdIdx]) !== identifier)]);
    setHighlightedKey(identifier);
    scrollToKey(identifier);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingJump, snapshot, snapshotError]);

  useEffect(() => {
    onRegisterJump(jumpToRow);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onRegisterJump, reportingId]);

  const isSearching = query.trim().length > 0 || !!column || activeFilters.length > 0;
  const meta = data
    ? isSearching
      ? `${data.rows.length} of ${data.total_matches} match${data.total_matches === 1 ? '' : 'es'}`
      : `${data.rows.length} of ${data.total_rows} rows`
    : undefined;
  const loadMoreCount = data ? Math.min(LOAD_MORE_INCREMENT, data.total_matches - data.rows.length) : 0;
  const hasMore = data ? data.rows.length < data.total_matches : false;

  const onRowToggle = (e) => {
    const keys = Object.keys(e.data || {});
    setExpandedKey(keys.length ? keys[keys.length - 1] : null);
  };

  return (
    <Dialog
      header={`LoanTape${meta ? ` — ${meta}` : ''}`}
      className="cr-loantape-dialog"
      visible={open}
      onHide={() => onOpenChange(false)}
      style={{ width: '95vw', maxWidth: '95vw' }}
      maximizable
      contentStyle={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}
    >
      <Toast ref={toast} />

      {/* Highlight style for a "jumped to" row (see jumpToRow below). Applied
          per-cell, not on <tr>, because `scrollable` mode renders th/td as
          flex items (see the Column `style` comment further down) - a
          border/background on the row element itself wouldn't show through
          its flex children. A single brand left-accent + tint reads as
          "this is the row you jumped to" without the boxy, glitchy look a
          dashed outline had around only the cells in view. */}
      <style>{`
        .loantape-highlighted-row > td {
          background-color: var(--app-primary-soft) !important;
          box-shadow: inset 0 1px 0 0 var(--primary-color), inset 0 -1px 0 0 var(--primary-color);
        }
        .loantape-highlighted-row > td:first-child {
          box-shadow: inset 4px 0 0 0 var(--primary-color), inset 0 1px 0 0 var(--primary-color), inset 0 -1px 0 0 var(--primary-color);
        }
      `}</style>

      <div className="flex flex-wrap align-items-center gap-2">
        <span className="p-input-icon-left flex-1" style={{ minWidth: 240 }}>
          <i className="pi pi-search" />
          <InputText value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search the LoanTape…" className="w-full" />
        </span>
        <Dropdown
          value={column ?? ALL_COLUMNS_VALUE}
          options={[{ label: 'All columns', value: ALL_COLUMNS_VALUE }, ...columns.map((c) => ({ label: c, value: c }))]}
          onChange={(e) => setColumn(e.value === ALL_COLUMNS_VALUE ? null : e.value)}
          style={{ width: 220 }}
        />
      </div>

      {activeFilters.length > 0 && (
        <div className="flex flex-wrap align-items-center gap-2">
          {activeFilters.map((filter) => (
            <Chip
              key={filter.column}
              label={`${filter.column}: ${summarizeFilter(filter)}`}
              removable
              onRemove={() => setActiveFilter(null, filter.column)}
            />
          ))}
          <Button label="Clear all filters" className="p-button-text p-button-sm" onClick={clearAllFilters} />
        </div>
      )}

      {snapshotLoading && <Skeleton height="16rem" borderRadius="12px" />}
      {snapshotError && <div className="ex-inline-error">{snapshotError}</div>}
      {searchError && <div className="ex-inline-error">{searchError}</div>}

      {data && (
        <DataTable
          value={tableRows}
          dataKey="_key"
          size="small"
          showGridlines
          scrollable
          scrollHeight="60vh"
          expandedRows={hasIdentifier && expandedKey ? { [expandedKey]: true } : null}
          onRowToggle={hasIdentifier ? onRowToggle : undefined}
          rowExpansionTemplate={hasIdentifier ? (rowData) => <LoanCashflowsPanel reportingId={reportingId} investmentKey={rowData._key} /> : undefined}
          rowClassName={(rowData) => ({
            'loantape-highlighted-row': rowData._highlighted,
          })}
        >
          {hasIdentifier && <Column expander style={{ flex: '0 0 2.5rem', width: '2.5rem' }} />}
          {columns.map((col, colIdx) => (
            <Column
              key={col}
              field={col}
              // PrimeReact's `scrollable` mode renders th/td as CSS flex
              // items (see primereact.css: `.p-datatable-scrollable ...
              // { display: flex; flex: 1 1 0; }`), NOT native table cells -
              // `table-layout`/plain `width` have no effect on a flex item's
              // actual size, only `flex-basis` (via `flex`) does. Every
              // column was defaulting to flex:1 1 0 (grow to fill, shrink to
              // fit, ignoring content) which is why columns weren't tracking
              // their content or the header at all. `flex: '0 0 180px'`
              // (no grow, no shrink, 180px basis) is what actually pins the
              // width; `overflow:hidden` + ellipsis below then keeps long
              // content from visually spilling into the next column.
              style={{ flex: '0 0 180px', width: '180px' }}
              header={
                <span
                  className="inline-flex align-items-center gap-1"
                  style={{ width: '100%', minWidth: 0, overflow: 'hidden' }}
                >
                  <span
                    style={{ flex: '1 1 auto', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    title={col}
                  >
                    {col}
                  </span>
                  {addedSet.has(col) && (
                    <span className="text-primary font-mono" style={{ fontSize: 9, backgroundColor: 'var(--app-primary-soft)', borderRadius: 4, padding: '0 4px', flexShrink: 0 }}>
                      f(x)
                    </span>
                  )}
                  <span style={{ flexShrink: 0 }}>
                    <ColumnFilterPopover snapshot={snapshot} column={col} activeFilters={activeFilters} debouncedQuery={debouncedQuery} searchColumn={column} onApply={setActiveFilter} />
                  </span>
                </span>
              }
              align={undefined}
              body={(rowData) => {
                const value = rowData[col];
                const display = formatCellValue(value);
                const inSearchScope = !column || col === column;
                const isMatch = !!debouncedQuery && inSearchScope && cellMatchesQuery(value, debouncedQuery);
                const marked = isMatch ? highlightMatch(display, debouncedQuery) : null;
                const isIdCol = colIdx === idColumnIndex;
                return (
                  <span
                    ref={isIdCol ? (el) => { if (el) rowRefs.current.set(rowData._key, el); } : undefined}
                    title={typeof display === 'string' ? display : undefined}
                    style={{
                      textAlign: typeof value === 'number' ? 'right' : 'left',
                      display: 'block',
                      width: '100%',
                      minWidth: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                    className={addedSet.has(col) ? 'cr-added-col-value' : undefined}
                  >
                    {marked ?? display}
                  </span>
                );
              }}
            />
          ))}
        </DataTable>
      )}

      {data && hasMore && (
        <div className="flex justify-content-center">
          <Button label={`Load ${loadMoreCount} more`} className="p-button-outlined p-button-sm" onClick={loadMore} />
        </div>
      )}
    </Dialog>
  );
};

export default LoanTapeViewerDialog;
